import { TelemetryPoint, TelemetrySeries } from '../models/dashboard.model';
import { parsePlatformTimestamp } from './company-date';
export const MAX_OBSERVED_GAP_MS = 60 * 60 * 1000;

export interface ObservedDurations {
  alarmMs: number;
  activeMs: number;
  hibernatingMs: number;
  stateObservedMs: number;
  hookedMs: number;
  unhookedMs: number;
  signalObservedMs: number;
}

const EMPTY_DURATIONS: ObservedDurations = {
  alarmMs: 0, activeMs: 0, hibernatingMs: 0, stateObservedMs: 0,
  hookedMs: 0, unhookedMs: 0, signalObservedMs: 0,
};

function orderedPoints(points: TelemetryPoint[]): TelemetryPoint[] {
  return [...points]
    .filter(point => Number.isFinite(parsePlatformTimestamp(point.time)) && Number.isFinite(Number(point.value)))
    .sort((left, right) => parsePlatformTimestamp(left.time) - parsePlatformTimestamp(right.time))
    .filter((point, index, all) => index === all.length - 1 || point.time !== all[index + 1].time);
}

function addObservedSegments(
  points: TelemetryPoint[],
  from: number,
  to: number,
  onSegment: (value: number, duration: number) => void,
): number {
  return orderedPoints(points).reduce((observed, point, index, all) => {
    const start = Math.max(from, parsePlatformTimestamp(point.time));
    const next = index + 1 < all.length ? parsePlatformTimestamp(all[index + 1].time) : to;
    const end = Math.min(to, next, parsePlatformTimestamp(point.time) + MAX_OBSERVED_GAP_MS);
    const duration = Math.max(0, end - start);
    if (duration) onSegment(Number(point.value), duration);
    return observed + duration;
  }, 0);
}

export function observedDurations(series: TelemetrySeries[], from: Date, to: Date): ObservedDurations {
  const durations = { ...EMPTY_DURATIONS };
  const start = from.getTime();
  const end = to.getTime();
  const deviceState = series.find(item => item.measure === 'device_state')?.points ?? [];
  const signalState = series.find(item => item.measure === 'state')?.points ?? [];

  durations.stateObservedMs = addObservedSegments(deviceState, start, end, (value, duration) => {
    if ((value & 0x01) !== 0) durations.alarmMs += duration;
    else if ((value & 0x02) !== 0) durations.activeMs += duration;
    else if (value === 0) durations.hibernatingMs += duration;
  });
  durations.signalObservedMs = addObservedSegments(signalState, start, end, (value, duration) => {
    if ((value & 0x01) !== 0) durations.hookedMs += duration;
    if ((value & 0x02) !== 0) durations.unhookedMs += duration;
  });

  return durations;
}

export function formatObservedDuration(duration: number): string {
  const minutes = Math.floor(duration / 60000);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function observedRatio(part: number, total: number): string {
  return total ? `${Math.round((part / total) * 100)}%` : '—';
}
